/** Floor-plan preview assets and the builders that stack them for the selected options. */

import { Language } from 'translations/Language'
import { Nullable } from 'models/helpers'

// 2D assets
import basic_black from 'assets/img/floorplan/2D/2D_commercial_bw.webp'
import basic_blue from 'assets/img/floorplan/2D/2D_commercial_blue.webp'
import basic_fixtures_black from 'assets/img/floorplan/2D/2D_commercial_bw_fixtures.webp'
import basic_fixtures_blue from 'assets/img/floorplan/2D/2D_commercial_blue_fixtures.webp'
import basic_fixtures_furniture_black from 'assets/img/floorplan/2D/2D_commercial_bw_fixtures_furniture.webp'
import basic_fixtures_furniture_blue from 'assets/img/floorplan/2D/2D_commercial_blue_fixtures_furniture.webp'
import basic_fixtures_furniture_green from 'assets/img/floorplan/2D/2D_commercial_green_fixtures_furniture.webp'
import basic_fixtures_furniture_neutral from 'assets/img/floorplan/2D/2D_commercial_neutral_fixtures_furniture.webp'
import basic_fixtures_green from 'assets/img/floorplan/2D/2D_commercial_green_fixtures.webp'
import basic_fixtures_neutral from 'assets/img/floorplan/2D/2D_commercial_neutral_fixtures.webp'
import basic_furniture_black from 'assets/img/floorplan/2D/2D_commercial_bw_furniture.webp'
import basic_furniture_blue from 'assets/img/floorplan/2D/2D_commercial_blue_furniture.webp'
import basic_furniture_green from 'assets/img/floorplan/2D/2D_commercial_green_furniture.webp'
import basic_furniture_neutral from 'assets/img/floorplan/2D/2D_commercial_neutral_furniture.webp'
import basic_green from 'assets/img/floorplan/2D/2D_commercial_green.webp'
import basic_neutral from 'assets/img/floorplan/2D/2D_commercial_neutral.webp'
// 3D assets
import basic_variant1 from 'assets/img/floorplan/3D/3D_basic_variant1.webp'
import basic_variant2 from 'assets/img/floorplan/3D/3D_basic_variant2.webp'
import basic_variant3 from 'assets/img/floorplan/3D/3D_basic_variant3.webp'
import three_fixtures_furniture_variant1 from 'assets/img/floorplan/3D/3D_fixtures_furniture_variant1.webp'
import three_fixtures_furniture_variant2 from 'assets/img/floorplan/3D/3D_fixtures_furniture_variant2.webp'
import three_fixtures_furniture_variant3 from 'assets/img/floorplan/3D/3D_fixtures_furniture_variant3.webp'
import three_fixtures_variant1 from 'assets/img/floorplan/3D/3D_fixtures_variant1.webp'
import three_fixtures_variant2 from 'assets/img/floorplan/3D/3D_fixtures_variant2.webp'
import three_fixtures_variant3 from 'assets/img/floorplan/3D/3D_fixtures_variant3.webp'
import three_furniture_variant1 from 'assets/img/floorplan/3D/3D_furniture_variant1.webp'
import three_furniture_variant2 from 'assets/img/floorplan/3D/3D_furniture_variant2.webp'
import three_furniture_variant3 from 'assets/img/floorplan/3D/3D_furniture_variant3.webp'
// shared assets
import compass from 'assets/img/floorplan/general/compass.webp'
import dimensions_chains from 'assets/img/floorplan/general/dimension_chains.webp'
import disclaimer_text from 'assets/img/floorplan/general/disclaimer_text.webp'
import logo_placeholder from 'assets/img/floorplan/general/logo_placeholder.webp'
import room_area from 'assets/img/floorplan/general/room_area.webp'
import room_names from 'assets/img/floorplan/general/room_names_ENG.webp'
import scale_bar from 'assets/img/floorplan/general/scale_bar.webp'

import { FloorPlanColors, FloorPlanFileOrientation, FloorPlanFormat, Orientations, Styles } from './floorPlan.enums'

/** Base options for configuring the display of a floor plan preview. */
export type FloorPlanBaseOptions = {
  showFixtures?: boolean
  showFurniture?: boolean
  showGardensAndRoofSlopes?: boolean
  showScalebar?: boolean
  showDimensionChain?: boolean
  showRoomArea?: boolean
  showRoomNames?: boolean
  showLogo?: boolean
  showCompass?: boolean
  showDisclaimer?: boolean
}

/** Options for a basic (2D) floor plan preview. */
export type FloorPlanBasicOptions = FloorPlanBaseOptions & {
  color: FloorPlanColors
}

/** Options for a 3D floor plan preview. */
export type FloorPlan3DOptions = FloorPlanBaseOptions & {
  style: Styles
  orientation: Orientations
}

/** A saved template's config options, applied to the draft on selection. */
export type FloorPlanConfigOptions = FloorPlanBaseOptions & {
  style: Styles | FloorPlanColors
  orientation?: Orientations
  companyLogo?: string
  outputLanguage?: Language
  floorPlanFormat?: Record<string, FloorPlanFormat>
  templateLogoFile?: Nullable<File>
  disclaimerText?: string
  fileOrientation?: Set<FloorPlanFileOrientation>
  isMultiStory?: boolean
}

// ─── 2D maps ──────────────────────────────────────────────────────────────────

const colorMap: Record<FloorPlanColors, string> = {
  [FloorPlanColors.BLACK]: basic_black,
  [FloorPlanColors.BLUE]: basic_blue,
  [FloorPlanColors.GREEN]: basic_green,
  [FloorPlanColors.NEUTRAL]: basic_neutral,
}

const colorFixturesMap: Record<FloorPlanColors, string> = {
  [FloorPlanColors.BLACK]: basic_fixtures_black,
  [FloorPlanColors.BLUE]: basic_fixtures_blue,
  [FloorPlanColors.GREEN]: basic_fixtures_green,
  [FloorPlanColors.NEUTRAL]: basic_fixtures_neutral,
}

const colorFurnitureMap: Record<FloorPlanColors, string> = {
  [FloorPlanColors.BLACK]: basic_furniture_black,
  [FloorPlanColors.BLUE]: basic_furniture_blue,
  [FloorPlanColors.GREEN]: basic_furniture_green,
  [FloorPlanColors.NEUTRAL]: basic_furniture_neutral,
}

const colorFixturesFurnitureMap: Record<FloorPlanColors, string> = {
  [FloorPlanColors.BLACK]: basic_fixtures_furniture_black,
  [FloorPlanColors.BLUE]: basic_fixtures_furniture_blue,
  [FloorPlanColors.GREEN]: basic_fixtures_furniture_green,
  [FloorPlanColors.NEUTRAL]: basic_fixtures_furniture_neutral,
}

// ─── 3D maps ──────────────────────────────────────────────────────────────────

/** Bird-view style previews. */
export const stylesMap: Record<Styles, string> = {
  [Styles.STYLE_1]: basic_variant1,
  [Styles.STYLE_2]: basic_variant2,
  [Styles.STYLE_3]: basic_variant3,
}

export const styleAngleViewMap: Record<Styles, string> = {
  [Styles.STYLE_1]: basic_variant1,
  [Styles.STYLE_2]: basic_variant2,
  [Styles.STYLE_3]: basic_variant3,
}

const styleFixturesMap: Record<Styles, string> = {
  [Styles.STYLE_1]: three_fixtures_variant1,
  [Styles.STYLE_2]: three_fixtures_variant2,
  [Styles.STYLE_3]: three_fixtures_variant3,
}

const styleFurnitureMap: Record<Styles, string> = {
  [Styles.STYLE_1]: three_furniture_variant1,
  [Styles.STYLE_2]: three_furniture_variant2,
  [Styles.STYLE_3]: three_furniture_variant3,
}

const styleFixturesFurnitureMap: Record<Styles, string> = {
  [Styles.STYLE_1]: three_fixtures_furniture_variant1,
  [Styles.STYLE_2]: three_fixtures_furniture_variant2,
  [Styles.STYLE_3]: three_fixtures_furniture_variant3,
}

// ─── Shared non-colored overlays ────────────────────────────────────────────────

const overlayLogo = logo_placeholder
const overlayRoomNames = room_names
const overlayDimensionChains = dimensions_chains
const overlayScalebar = scale_bar
const overlayRoomArea = room_area
const overlayCompass = compass
const overlayDisclaimer = disclaimer_text

// ─── Preview builders ────────────────────────────────────────────────────────

/** 2D preview image URLs for the selected options . */
export const getFloorPlanPreviewImages = ({
  color,
  showDimensionChain,
  showScalebar,
  showFurniture,
  showLogo,
  showRoomArea,
  showRoomNames,
  showCompass,
  showFixtures,
  showDisclaimer,
}: FloorPlanBasicOptions): string[] => {
  const images: string[] = []

  if (showDisclaimer) images.push(overlayDisclaimer)
  if (showFurniture && !showFixtures) images.push(colorFurnitureMap[color])
  if (showFixtures && !showFurniture) images.push(colorFixturesMap[color])
  if (showFurniture && showFixtures) images.push(colorFixturesFurnitureMap[color])
  if (!showFixtures && !showFurniture) images.push(colorMap[color])
  if (showDimensionChain) images.push(overlayDimensionChains)
  if (showScalebar) images.push(overlayScalebar)
  if (showLogo) images.push(overlayLogo)
  if (showRoomArea) images.push(overlayRoomArea)
  if (showRoomNames) images.push(overlayRoomNames)
  if (showCompass) images.push(overlayCompass)

  return images
}

/** 3D preview image URLs for the selected options . */
export const getPreview = ({
  style,
  orientation,
  showDimensionChain,
  showScalebar,
  showFurniture,
  showLogo,
  showRoomArea,
  showRoomNames,
  showCompass,
  showFixtures,
  showDisclaimer,
}: FloorPlan3DOptions): string[] => {
  const images: string[] = []

  if (showFurniture && !showFixtures) images.push(styleFurnitureMap[style])
  if (showFixtures && !showFurniture) images.push(styleFixturesMap[style])
  if (showFurniture && showFixtures) images.push(styleFixturesFurnitureMap[style])
  if (!showFixtures && !showFurniture && orientation === Orientations.BIRD_VIEW) images.push(stylesMap[style])
  if (!showFixtures && !showFurniture && orientation === Orientations.ANGLE_VIEW) images.push(styleAngleViewMap[style])
  if (showDimensionChain) images.push(overlayDimensionChains)
  if (showScalebar) images.push(overlayScalebar)
  if (showLogo) images.push(overlayLogo)
  if (showRoomArea) images.push(overlayRoomArea)
  if (showRoomNames) images.push(overlayRoomNames)
  if (showCompass) images.push(overlayCompass)
  if (showDisclaimer) images.push(overlayDisclaimer)

  return images
}
